import { useRef, useState } from 'react'
import type * as monaco from 'monaco-editor'
import { Check, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { TabBar } from '@/components/TabBar'
import { YamlMonacoEditor } from '@/components/projects/workflows'
import { AgentConfigForm, AgentMemoryScopeForm } from '@/components/projects'
import { AGENT_YAML_MAX, serializeAgentYaml } from '@/lib/agentYaml'
import { useAgentDetailContext } from '@/hooks'

type ConfigView = 'yaml' | 'form' | 'memory-scope'

const configViewTabs: { key: ConfigView; label: string }[] = [
  { key: 'yaml', label: 'YAML' },
  { key: 'form', label: 'Form' },
  { key: 'memory-scope', label: 'Memory Scope' },
]

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

export function AgentConfigPage() {
  const {
    projectId,
    agent,
    canEdit,
    editorYaml,
    onYamlChange,
    parsedConfig,
    unknownYamlKeys,
    displayName,
    onDisplayNameChange,
    description,
    onDescriptionChange,
    missingRequiredFields,
    isSaving,
    isRefreshing,
    saveBlockedReason,
    onSave,
    onExport,
  } = useAgentDetailContext()

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const [configView, setConfigView] = useState<ConfigView>('yaml')

  const yamlTooLong = editorYaml.length > AGENT_YAML_MAX

  return (
    <div className="space-y-4" data-testid="agent-config-section">
      <div className="space-y-5 rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onExport}>
            <Download className="h-4 w-4" />
            Export YAML
          </Button>
          {canEdit && (
            <Button
              onClick={onSave}
              disabled={saveBlockedReason !== null || isSaving || isRefreshing}
              title={saveBlockedReason ?? undefined}
              data-testid="save-agent-button"
            >
              <Check className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Agent'}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-start gap-6">
          <div className="min-w-[240px] flex-1 space-y-2">
            <Label htmlFor="agent-display-name" className={labelClass}>
              Display Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="agent-display-name"
              value={displayName}
              onChange={e => onDisplayNameChange(e.target.value)}
              disabled={!canEdit}
              maxLength={256}
              className="h-10 rounded-lg"
              data-testid="agent-display-name-input"
            />
            {canEdit && !displayName.trim() && (
              <p className="text-xs text-red-500" role="alert">Display name is required</p>
            )}
          </div>
          <div className="min-w-[240px] flex-1 space-y-2">
            <Label htmlFor="agent-internal-name" className={labelClass}>Internal Name</Label>
            <Input
              id="agent-internal-name"
              value={agent.name}
              readOnly
              disabled
              className="h-10 rounded-lg border-input font-mono"
              data-testid="agent-internal-name-input"
              aria-describedby="agent-internal-name-message"
            />
            <p id="agent-internal-name-message" className="text-xs text-muted-foreground">
              Workflows reference the agent by this name, so it can't be changed yet.
            </p>
          </div>
          <div className="min-w-[140px] space-y-2">
            <Label htmlFor="agent-version" className={labelClass}>Version</Label>
            <Input
              id="agent-version"
              value={agent.latestVersion?.version ?? '—'}
              readOnly
              disabled
              className="h-10 w-[140px] rounded-lg font-mono"
              data-testid="agent-version-input"
            />
            <p className="text-xs text-muted-foreground">Assigned automatically on save.</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-description" className={labelClass}>Description</Label>
          <Textarea
            id="agent-description"
            value={description}
            onChange={e => onDescriptionChange(e.target.value)}
            disabled={!canEdit}
            maxLength={2000}
            rows={2}
            data-testid="agent-description-input"
          />
        </div>
      </div>

      <TabBar tabs={configViewTabs} activeTab={configView} onTabChange={setConfigView} className="w-fit" />

      {!parsedConfig && (
        <p className="text-sm text-destructive" role="alert" data-testid="yaml-invalid">
          The YAML is invalid, so it can't be saved. Fix the syntax in the YAML tab.
        </p>
      )}
      {yamlTooLong && (
        <p className="text-sm text-destructive" role="alert">
          Agent YAML must not exceed {AGENT_YAML_MAX} characters (currently {editorYaml.length}).
        </p>
      )}
      {parsedConfig && missingRequiredFields.length > 0 && (
        <p className="text-sm text-destructive" role="alert" data-testid="missing-required-fields">
          Required before saving: {missingRequiredFields.join(' and ')}.
        </p>
      )}

      {configView === 'yaml' && (
        <div className="space-y-2">
          <div className="h-[560px] overflow-hidden rounded-2xl border border-border">
            <YamlMonacoEditor
              value={editorYaml}
              onChange={onYamlChange}
              editorRef={editorRef}
              readOnly={!canEdit}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Every save creates an immutable version — workflows pin a version, so editing never silently changes a running workflow.
            </span>
            <span className={yamlTooLong ? 'font-bold text-destructive' : ''} data-testid="yaml-char-count">
              {editorYaml.length}/{AGENT_YAML_MAX}
            </span>
          </div>
        </div>
      )}

      {configView === 'form' && (
        parsedConfig ? (
          <>
            {unknownYamlKeys.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200" data-testid="unknown-yaml-keys-warning">
                Editing here rewrites the YAML from the form fields — comments, formatting and keys the form doesn't know ({unknownYamlKeys.join(', ')}) will be dropped. Use the YAML tab to keep them.
              </div>
            )}
            <AgentConfigForm
              projectId={projectId}
              config={parsedConfig}
              onChange={next => onYamlChange(serializeAgentYaml(next))}
              disabled={!canEdit}
            />
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="form-yaml-invalid">
            Fix the YAML syntax to edit these fields.
          </div>
        )
      )}

      {configView === 'memory-scope' && (
        parsedConfig ? (
          <AgentMemoryScopeForm
            config={parsedConfig}
            onChange={next => onYamlChange(serializeAgentYaml(next))}
            disabled={!canEdit}
          />
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="memory-scope-yaml-invalid">
            Fix the YAML syntax to edit the memory scope.
          </div>
        )
      )}
    </div>
  )
}
